<template>
  <div v-auto-animate v-if="app.cities">
    <div class="flex items-center justify-between mb-2">
      <p class="text-lg font-bold">{{ $t('fat.location.headingAll') }}</p>
      <div class="flex items-center gap-2">
        <div class="flex items-center rounded-full border overflow-hidden">
          <div class="px-3 flex items-center h-full cursor-pointer transition-all border-r" :class="{ 'bg-mist': sortBy === 'name', 'opacity-50': sortBy !== 'name' }" @click="setSort('name')">
            <AtomIcon name="arrow-down-a-z" class="h-6 text-sm" v-if="orderBy === 'asc'" />
            <AtomIcon name="arrow-up-a-z" class="h-6 text-sm" v-else />
          </div>
          <div class="px-3 flex items-center h-full cursor-pointer transition-all border-r" :class="{ 'bg-mist': sortBy === 'count', 'opacity-50': sortBy !== 'count' }" @click="setSort('count')">
            <AtomIcon name="arrow-down-wide-short" class="h-6 text-sm" v-if="orderBy === 'desc'" />
            <AtomIcon name="arrow-up-wide-short" class="h-6 text-sm" v-else />
          </div>
        </div>
      </div>
    </div>
    <div class="mb-2 relative">
      <div class="absolute left-1.5 top-1/2 -translate-y-1/2 opacity-50">
        <AtomIcon name="magnifying-glass" class="h-6 w-6 text-sm" />
      </div>
      <input type="text" v-model="searchPhrase" :placeholder="$t('fat.location.searchField')" class="w-full pl-8 pr-2 py-1 border rounded-sm" ref="inputRef" />
      <div class="absolute right-1 top-1/2 -translate-y-1/2">
        <AtomIcon name="circle-xmark" class="h-6 w-6 text-sm cursor-pointer" @click="searchPhrase = ''" v-if="searchPhrase !== ''" />
      </div>
    </div>
    <div class="grid gap-1" v-auto-animate>
      <div v-for="city in sortedCities" :key="city.id" class="bg-mist rounded-md transition-all hover:text-coral" :class="{ '!bg-coral !text-white': fat.cityId === city.id && (!fat.location || !route.name.startsWith('fat')) }">
        <div class="flex items-center justify-between cursor-pointer py-2 px-2" @click="updateCityId(city.id)">
          <div class="font-medium text-sm">
            {{ city.name }}
          </div>
          <div class="flex items-center gap-4">
            <div class="text-[#888] text-sm" :class="{ 'text-white': fat.cityId === city.id }" v-if="city.restaurants >= 4 && sortBy === 'count'">
              {{ city.restaurants }}
            </div>
            <AtomIcon name="chevron-right" :class="{ 'text-coral': fat.cityId !== city.id }" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const route = useRoute()
const app = useAppStore()
const fat = useFatStore()
const inputRef = ref()
const searchPhrase = ref('')
const sortBy = ref('name')
const orderBy = ref('asc')

// Update City ID
const updateCityId = (cityId) => {
  const city = useCityById(cityId)
  const reRoutePages = [
    'restaurants', 
    'restaurants-city', 
    'restaurants-city-tag', 
    'events', 
    'events-city',
    'events-city-tag',
    'offers', 
    'offers-city',
    'giftcards', 
    'giftcards-city',
  ]
  
  const cityRouteName = route.name.split('-')[0] + '-city'
  const baseSlug = route.path.split('/')[1] ?? null
  const category = app.categories.find(cat => cat.slug === baseSlug)

  if (route.name.startsWith('fat') || route.name === 'res-req') {
    // console.log('FAT || ResREq')
    fat.cityId = parseInt(cityId)

    return navigateTo({
      query: {
        ...route.query,
        cityId: cityId,
        drawer: undefined,
        location: undefined,
        locationModal: undefined
      }
    })
  } else if (city && reRoutePages.includes(route.name)) {
    // console.log('reRoutePages')
    fat.cityId = parseInt(cityId)

    return navigateTo({
      name: cityRouteName,
      params: {
        city: useSlugify(city.name)
      },
      query: {
        ...route.query,
        drawer: undefined,
        location: undefined,
        locationModal: undefined
      }
    })
  } else {
    // console.log('else')
    fat.cityId = parseInt(cityId)
    
    return navigateTo({ 
      path: `/${city.slug}`,
      query: {
        ...route.query,
        drawer: undefined,
        location: undefined,
        locationModal: undefined
      }
    })
  }
}

// Filter and sort the cities
const sortedCities = computed(() => {
  let result = [...app.cities].slice(3)

  // Filter by search phrase if provided
  if (searchPhrase.value) {
    const searchPhraseLowerCase = searchPhrase.value?.toLowerCase()
    result = result.filter(city => city.name?.toLowerCase()?.includes(searchPhraseLowerCase))
  }

  // Sort the array based on restaurants
  if (sortBy.value === 'count') {
    if (orderBy.value === 'asc') {
      result.sort((a, b) => a.restaurants - b.restaurants)
    }

    if (orderBy.value === 'desc') {
      result.sort((a, b) => b.restaurants - a.restaurants)
    }
  }

  // Sort the array based on name
  if (sortBy.value === 'name') {
    if (orderBy.value === 'asc') {
      result.sort((a, b) => a.name.localeCompare(b.name, 'sv'))
    }
    
    if (orderBy.value === 'desc') {
      result.sort((a, b) => b.name.localeCompare(a.name, 'sv'))
    }
  }

  return result
})

// Set sort attribute, or switch order
const setSort = (value) => {
  if (sortBy.value === value) {
    orderBy.value === 'desc'
      ? orderBy.value = 'asc'
      : orderBy.value = 'desc'
  } else {
    sortBy.value = value
   
    if (value === 'name') {
      orderBy.value = 'asc'
    }
    
    if (value === 'count') {
      orderBy.value = 'desc'
    }
  }
}
</script>

<style lang="scss" scoped>
// 
</style>